/**
 * useSubscribeUser
 *
 * Orchestrates the SubscribeUserSheet bottom-sheet UI:
 *   - Fetches the target user (if not preloaded) and the
 *     "shouldSendSubscriptionRequest" flag in parallel on mount.
 *   - Snapshots the exclusive-content subscription status on mount (matching
 *     Flutter's `initState` capture in
 *     `lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart:63-65`).
 *   - Computes title / description / positive-button text / price string
 *     mirroring Flutter's `_setBottomSheetValues` (lines 131-213).
 *   - On positive-press, runs the insufficient-coins gate (Flutter:613-634)
 *     and dispatches `ToggleExclusiveSubscriptionUseCase`.
 *
 * Closes the sheet after the subscribe call completes (success OR failure)
 * to mirror Flutter's `_closeBottomSheet()` at line 637.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import type { RootState } from '@/presentation/store';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import { selectExclusiveContentSubscriptionStatus } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import { selectWalletCoins } from '@/presentation/store/slices/wallet/walletSlice';
import { createFetchUserDetailsUseCase } from '@/domain/usecases/user/fetchUserDetailsUseCase';
import { createGetShouldSendSubscriptionRequestUseCase } from '@/domain/usecases/exclusiveContentSubscription/getShouldSendSubscriptionRequestUseCase';
import { createToggleExclusiveSubscriptionUseCase } from '@/domain/usecases/exclusiveContentSubscription/toggleExclusiveSubscriptionUseCase';
import { MathUtils } from '@/domain/utils/mathUtils';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { buildSubscribeUserCopy } from '../buildSubscribeUserCopy';

export interface UseSubscribeUserParams {
  targetFirebaseId: string;
  preloadedUser?: UserEntity | null;
  screenKey?: string;
}

export interface UseSubscribeUserResult {
  title: string;
  description: string;
  positiveText: string;
  showCoinsCharge: boolean;
  priceText: string;
  fetching: boolean;
  isSubscribing: boolean;
  onPositive: () => Promise<void>;
  onClose: () => void;
}

export const useSubscribeUser = (
  params: UseSubscribeUserParams,
): UseSubscribeUserResult => {
  const { targetFirebaseId, preloadedUser, screenKey } = params;
  const { l10n } = useLocale();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Snapshot status on mount (Flutter captures in initState, lines 63-65).
  // Using a state initializer ensures this runs once per hook instance.
  const initialStatus = useSelector((state: RootState) =>
    selectExclusiveContentSubscriptionStatus(state, targetFirebaseId),
  );
  const [snapshottedStatus] = useState<ExclusiveContentSubscriptionStatus>(initialStatus);

  // Wallet coins is read fresh whenever the user taps the positive button.
  const walletCoins = useSelector(selectWalletCoins);

  // Local state
  const [targetUser, setTargetUser] = useState<UserEntity | null>(
    preloadedUser ?? null,
  );
  const [shouldSendRequest, setShouldSendRequest] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(true);
  const [isSubscribing, setIsSubscribing] = useState<boolean>(false);

  // Fetch on mount: user (if not preloaded) and shouldSendRequest in parallel.
  useEffect(() => {
    let cancelled = false;

    const maybeFetchUser = async (): Promise<void> => {
      if (preloadedUser) return;
      try {
        const user = await createFetchUserDetailsUseCase().execute(targetFirebaseId);
        if (!cancelled) setTargetUser(user);
      } catch (error) {
        appLogger.error(
          'Failed to fetch target user for subscribe sheet',
          'useSubscribeUser.maybeFetchUser', { context: { targetFirebaseId }, error: error as Error },
        );
      }
    };

    const fetchShouldSendRequest = async (): Promise<void> => {
      try {
        const result = await createGetShouldSendSubscriptionRequestUseCase().execute(
          targetFirebaseId,
          snapshottedStatus,
        );
        if (!cancelled) setShouldSendRequest(result);
      } catch (error) {
        appLogger.error(
          'Failed to resolve shouldSendSubscriptionRequest',
          'useSubscribeUser.fetchShouldSendRequest', { context: { targetFirebaseId, snapshottedStatus }, error: error as Error },
        );
      }
    };

    Promise.all([maybeFetchUser(), fetchShouldSendRequest()]).finally(() => {
      if (!cancelled) setFetching(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Computed copy bundle — see `buildSubscribeUserCopy` for the parity machine.
  const copy = useMemo(
    () => buildSubscribeUserCopy(targetUser, snapshottedStatus, shouldSendRequest, l10n),
    [targetUser, snapshottedStatus, shouldSendRequest, l10n],
  );

  const onClose = useCallback(() => {
    SheetService.close();
  }, []);

  const onPositive = useCallback(async () => {
    // Flutter line 225 guard
    if (isSubscribing) return;
    // Flutter:221-224 guard — silently bail on self / blocked / suspended so
    // the rejection doesn't surface as a "There was an error" toast below.
    if (!targetUser || !currentUser) return;
    if (
      targetUser.firebaseId === currentUser.firebaseId ||
      targetUser.blocked ||
      targetUser.suspended
    ) {
      return;
    }

    // Insufficient-coins gate (Flutter:613-634) applies to inactive/cancelled.
    const isSubscribeFlow =
      snapshottedStatus === ExclusiveContentSubscriptionStatus.inactive ||
      snapshottedStatus === ExclusiveContentSubscriptionStatus.cancelled;

    if (
      isSubscribeFlow &&
      MathUtils.coinsToUSD(walletCoins) < targetUser.subscriptionCharge
    ) {
      InfoSheet.show({
        title: l10n.subscribeUser.insufficientCoinsTitle,
        infoText: l10n.subscribeUser.insufficientCoinsMessage,
        screenKey,
      });
      // Do NOT close the subscribe sheet — Flutter's _closeBottomSheet runs
      // after `_subscribeToUserExclusiveContent`, not in this early-return.
      return;
    }

    setIsSubscribing(true);

    let success = false;
    try {
      success = await createToggleExclusiveSubscriptionUseCase().execute({
        targetUserId: targetFirebaseId,
        status: snapshottedStatus,
        shouldSendRequest,
        currentUserId: currentUser.firebaseId,
        targetUser: {
          firebaseId: targetUser.firebaseId,
          blocked: targetUser.blocked,
          suspended: targetUser.suspended,
        },
      });
    } catch (error) {
      appLogger.error(
        'toggleExclusiveSubscription threw',
        'useSubscribeUser.onPositive', { context: { targetFirebaseId, status: snapshottedStatus, shouldSendRequest }, error: error as Error },
      );
      success = false;
    }

    if (!success) {
      toast.error(l10n.subscribeUser.genericError);
    }

    // Always close on completion (success or failure) — Flutter line 637.
    SheetService.close();

    setIsSubscribing(false);
  }, [
    isSubscribing,
    targetUser,
    currentUser,
    snapshottedStatus,
    walletCoins,
    shouldSendRequest,
    targetFirebaseId,
    screenKey,
    l10n,
  ]);

  return {
    title: copy.title,
    description: copy.description,
    positiveText: copy.positiveText,
    showCoinsCharge: copy.showCoinsCharge,
    priceText: copy.priceText,
    fetching,
    isSubscribing,
    onPositive,
    onClose,
  };
};
